import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { Seo } from '@/layout/Seo'
import { HideTopNavigationLayout } from '@/layouts/HideTopNavigationLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import {
  CatalogSeason,
  CatalogSeries,
  CatalogTitle,
  fetchTitle,
  findTitleProjectSlug,
  isEpisode,
} from '@/services/ContentCatalog'
import { getShareLink, ShareLink } from '@/services/ContentShareService'
import { getProjectAbout, ProjectMeta } from '@/services/ProjectsService'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { omitUndefineds } from '@/utils/object'
import { getBasePath } from '@/utils/sitemap/base'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PerfLogsProvider } from '@/utils/usePerfLogs'
import { ContentSharedViewDisplayFields, OneToManyContentSharedView } from '@/views/ContentSharedView'

interface PageProps {
  id: string
  locale: string
  shareLink: ShareLink
  watchableTitle: CatalogTitle
  displayFields: ContentSharedViewDisplayFields
  projectMeta: ProjectMeta | null
}

// NOTE This page is for 1:many sharing, and is a very different experience than 1:1 sharing. See /watch/share for 1:1
export const OneToManyContentSharedPage: NextPageWithLayout<PageProps> = (props) => {
  const { shareLink, id, locale, watchableTitle, displayFields, projectMeta } = props
  const path = `${paths.watch.index}/shared/${id}`
  const url = `${getBasePath(locale)}${path}`

  const metadata = shareLink.metadata
  const description = metadata.opengraph_description
  const title = metadata.opengraph_title
  const images = [
    {
      url: metadata.opengraph_image,
      width: 1200,
      alt: description,
    },
  ]

  return (
    <>
      <Seo
        canonical={url}
        description={description}
        openGraph={{ title, description, url, images }}
        path={path}
        title={title}
        nofollow
        noindex
      />
      <PerfLogsProvider>
        <ProjectMetaProvider project={projectMeta} catalogTitle={watchableTitle}>
          <MarketingVideoPopup />
          <OneToManyContentSharedView
            shareLink={shareLink}
            watchableTitle={watchableTitle}
            displayFields={displayFields}
          />
        </ProjectMetaProvider>
      </PerfLogsProvider>
    </>
  )
}

OneToManyContentSharedPage.getLayout = (page) => {
  return <HideTopNavigationLayout isDarkMode={true}>{page}</HideTopNavigationLayout>
}

export const getStaticPaths = noPreRenderedStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<PageProps>(
  { filePath: 'pages/[locale]/watch/shared/[id]/index.tsx' },
  async ({ locale, params, next404, federationClient, preview }) => {
    const id = getItemOrFirstEntry(params.id)
    if (!id) {
      return {
        redirect: {
          destination: paths.watch.index,
          permanent: false,
        },
      }
    }

    const shareLink = await getShareLink({ id })
    if (!shareLink) {
      return next404(Durations.ONE_MINUTE_IN_SECONDS)
    }

    const raw = await fetchTitle<CatalogTitle>(locale, shareLink.content_id)
    const watchableTitle = omitUndefineds(raw)

    if (!watchableTitle) {
      return next404(Durations.ONE_MINUTE_IN_SECONDS)
    }

    const projectSlug = findTitleProjectSlug(watchableTitle)

    const [projectMeta, displayFields, translations] = await Promise.all([
      getProjectAbout({ slug: projectSlug }, federationClient, { locale, preview }),
      getDisplayFields(locale, watchableTitle),
      loadTranslations(locale, ['common', 'watch', 'guild']),
    ])

    return {
      props: {
        displayFields,
        shareLink,
        locale,
        id,
        watchableTitle,
        projectMeta,
        ...translations,
      },
      revalidate: Durations.THIRTY_MINUTES_IN_SECONDS,
    }
  },
)

async function getDisplayFields(locale: string, watchableTitle: CatalogTitle): Promise<ContentSharedViewDisplayFields> {
  if (isEpisode(watchableTitle)) {
    const episodeTitle = watchableTitle
    const seasonTitle = await fetchTitle<CatalogSeason>(locale, episodeTitle.seasonId)
    const seriesTitle = seasonTitle?.seriesId
      ? await fetchTitle<CatalogSeries>(locale, seasonTitle.seriesId)
      : undefined

    const subTitle =
      typeof seasonTitle?.number === 'number' && typeof episodeTitle?.number === 'number' && seriesTitle?.title
        ? `S${seasonTitle.number}:E${episodeTitle.number} ${seriesTitle?.title}`
        : undefined

    return { title: episodeTitle.title, subTitle }
  }
  return { title: watchableTitle.title }
}

export default OneToManyContentSharedPage
